import React, { useState, useMemo } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Checkbox } from '../ui/checkbox';
import {
  DollarSign, Star, TrendingDown, Settings, CheckCircle2, Save, ArrowLeft,
  Calculator, AlertTriangle, BarChart3, Receipt
} from 'lucide-react';

const pricingPlans = [
  {
    id: 'zero_fee',
    name: 'Zero-Fee',
    subtitle: 'Pass fees to customers',
    icon: Star,
    iconBg: 'bg-orange-100',
    iconColor: 'text-orange-500',
    rate: '4.0%',
    rateLabel: 'Customer pays processing fee',
    rateBg: 'bg-orange-50 border-orange-200',
    rateColor: 'text-orange-600',
    benefits: ['No monthly fees', 'Customers pay processing', 'Increase profit margins'],
  },
  {
    id: 'flat_rate',
    name: 'Flat Rate',
    subtitle: 'Simple, predictable pricing',
    icon: TrendingDown,
    iconBg: 'bg-blue-100',
    iconColor: 'text-[#3b82f6]',
    rate: '2.69%',
    rateExtra: '+ $0.10 per transaction',
    rateBg: 'bg-blue-50 border-blue-200',
    rateColor: 'text-[#3b82f6]',
    recommended: true,
    benefits: ['One simple rate', 'Easy to calculate costs', 'No hidden fees'],
  },
  {
    id: 'custom',
    name: 'Custom',
    subtitle: 'Tailored to your needs',
    icon: Settings,
    iconBg: 'bg-purple-100',
    iconColor: 'text-purple-500',
    rate: 'Variable',
    rateLabel: 'Based on card type',
    rateBg: 'bg-purple-50 border-purple-200',
    rateColor: 'text-purple-600',
    benefits: ['Interchange + pricing', 'Qualified rates', 'Volume discounts'],
  }
];

function SectionHeader({ children }) {
  return <div className="bg-gray-100 text-xs font-bold uppercase tracking-wider px-4 py-2.5 text-center border rounded-t">{children}</div>;
}

function FeeInput({ label, value, onChange, testId }) {
  return (
    <div>
      <Label className="text-xs mb-1 block">{label}</Label>
      <div className="relative">
        <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground text-xs">$</span>
        <Input type="number" step="0.01" value={value || ''} onChange={(e) => onChange(e.target.value)} className="pl-6 h-8 text-sm" data-testid={testId} />
      </div>
    </div>
  );
}

function DollarInput({ label, value, onChange, testId, placeholder }) {
  return (
    <div>
      <Label className="text-sm mb-1 block">{label}</Label>
      <div className="relative">
        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">$</span>
        <Input type="number" step="0.01" value={value} onChange={(e) => onChange(e.target.value)} className="pl-7" placeholder={placeholder || '0.00'} data-testid={testId} />
      </div>
    </div>
  );
}

function Step3Pricing({ application, onNext, onPrevious, saving, onSaveDraft }) {
  const section = application.sections?.find(s => s.step_number === 3) || {};
  const [formData, setFormData] = useState({
    pricing_program: '',
    // Annual & Monthly Volume Breakdown
    annual_processing_volume: '',
    monthly_avg_visa_mc: '',
    monthly_avg_discover: '',
    monthly_avg_amex: '',
    avg_ticket: '',
    high_ticket: '',
    // Legacy Processing Volume
    avg_monthly_volume: '',
    avg_transaction_amount: '',
    // Delivery Windows
    delivery_0_7: false,
    delivery_8_14: false,
    delivery_15_30: false,
    delivery_30_plus: false,
    // Mode of Transaction
    mode_in_person: '',
    mode_telephone: '',
    mode_online: '',
    // Detailed Pricing
    detailed_program: '',
    // Interchange Plus
    interchange_basis_points: '',
    interchange_transaction_fee: '',
    interchange_type: '',
    interchange_dues_passthru: false,
    // Fees and Charges
    monthly_service_fee: '',
    monthly_debit_access: '',
    batch_fee: '',
    avs_fee: '',
    monthly_minimum: '',
    monthly_equipment_subscription: '',
    gateway_fee: '',
    gateway_trans_fee: '',
    wireless_monthly: '',
    wireless_per_item: '',
    pci_fee: '',
    non_pci_compliance_fee: '',
    annual_advantage_fee: '',
    monthly_advantage_program: '',
    ...section.data
  });

  const set = (field, value) => setFormData(prev => ({ ...prev, [field]: value }));

  const modeTotal = useMemo(() => {
    const a = parseFloat(formData.mode_in_person) || 0;
    const b = parseFloat(formData.mode_telephone) || 0;
    const c = parseFloat(formData.mode_online) || 0;
    return a + b + c;
  }, [formData.mode_in_person, formData.mode_telephone, formData.mode_online]);

  const monthlyBreakdownTotal = useMemo(() => {
    const vs = parseFloat(formData.monthly_avg_visa_mc) || 0;
    const ds = parseFloat(formData.monthly_avg_discover) || 0;
    const amex = parseFloat(formData.monthly_avg_amex) || 0;
    return vs + ds + amex;
  }, [formData.monthly_avg_visa_mc, formData.monthly_avg_discover, formData.monthly_avg_amex]);

  const pricingExample = useMemo(() => {
    const txAmount = 50;
    let fee = 0;
    if (formData.pricing_program === 'zero_fee') fee = txAmount * 0.04;
    else if (formData.pricing_program === 'flat_rate') fee = txAmount * 0.0269 + 0.10;
    else if (formData.pricing_program === 'custom') fee = txAmount * 0.018;
    return { txAmount, fee: fee.toFixed(2), net: (txAmount - fee).toFixed(2) };
  }, [formData.pricing_program]);

  const handleSubmit = (e) => {
    e.preventDefault();
    onNext(formData);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Section Header */}
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-lg bg-[#3b82f6] flex items-center justify-center shrink-0">
          <DollarSign className="h-5 w-5 text-white" />
        </div>
        <div>
          <h3 className="text-lg font-semibold" data-testid="step3-title">Pricing Program</h3>
          <p className="text-sm text-muted-foreground">Choose the pricing structure and provide volume details</p>
        </div>
      </div>

      {/* Pricing Cards */}
      <div className="grid gap-4 md:grid-cols-3">
        {pricingPlans.map((plan) => {
          const isSelected = formData.pricing_program === plan.id;
          const IconComp = plan.icon;
          return (
            <div
              key={plan.id}
              onClick={() => set('pricing_program', plan.id)}
              className={`relative border-2 rounded-xl p-5 cursor-pointer transition-all hover:shadow-md ${
                isSelected ? 'border-[#3b82f6] bg-blue-50/30 shadow-md' : 'border-border hover:border-[#3b82f6]/40'
              }`}
              data-testid={`pricing-card-${plan.id}`}
            >
              {plan.recommended && (
                <div className="absolute top-3 right-3">
                  <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-green-100 text-green-700 uppercase tracking-wide">Recommended</span>
                </div>
              )}
              <div className="flex flex-col items-center text-center space-y-3">
                <div className={`w-12 h-12 rounded-full ${plan.iconBg} flex items-center justify-center`}>
                  <IconComp className={`h-6 w-6 ${plan.iconColor}`} />
                </div>
                <div>
                  <div className="font-semibold text-base">{plan.name}</div>
                  <div className="text-xs text-muted-foreground">{plan.subtitle}</div>
                </div>
                <div className={`border rounded-lg px-4 py-2 w-full ${plan.rateBg}`}>
                  <div className={`text-2xl font-bold ${plan.rateColor}`}>{plan.rate}</div>
                  {plan.rateExtra && <div className="text-xs text-muted-foreground">{plan.rateExtra}</div>}
                  {plan.rateLabel && <div className="text-xs text-muted-foreground">{plan.rateLabel}</div>}
                </div>
                <div className="space-y-1.5 text-left w-full">
                  {plan.benefits.map((b, i) => (
                    <div key={i} className="flex items-center gap-2 text-sm">
                      <CheckCircle2 className="h-3.5 w-3.5 shrink-0 text-green-500" />
                      <span>{b}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Pricing Impact Example */}
      {formData.pricing_program && (
        <div className="border rounded-xl p-5 bg-blue-50/30">
          <div className="flex items-center gap-2 mb-4">
            <Calculator className="h-5 w-5 text-[#3b82f6]" />
            <span className="font-semibold">Pricing Impact Example</span>
          </div>
          <div className="grid grid-cols-3 gap-4">
            <div className="text-center p-3 bg-white rounded-lg border">
              <div className="text-xl font-bold">${pricingExample.txAmount}</div>
              <div className="text-xs text-muted-foreground">Transaction Amount</div>
            </div>
            <div className="text-center p-3 bg-white rounded-lg border">
              <div className="text-xl font-bold text-red-500">${pricingExample.fee}</div>
              <div className="text-xs text-muted-foreground">Processing Fee</div>
            </div>
            <div className="text-center p-3 bg-white rounded-lg border">
              <div className="text-xl font-bold text-green-600">${pricingExample.net}</div>
              <div className="text-xs text-muted-foreground">Net Amount</div>
            </div>
          </div>
        </div>
      )}

      {/* Annual & Monthly Volume Breakdown */}
      <div className="border rounded-lg overflow-hidden">
        <SectionHeader>Processing Volume Breakdown</SectionHeader>
        <div className="p-4 space-y-4">
          <DollarInput label="Annual Processing Volume" value={formData.annual_processing_volume} onChange={(v) => set('annual_processing_volume', v)} testId="annual-processing-volume" placeholder="Total yearly volume" />
          <div>
            <Label className="text-sm font-medium mb-2 block flex items-center gap-1.5">
              <BarChart3 className="h-3.5 w-3.5 text-[#3b82f6]" /> Monthly Average by Card Brand
            </Label>
            <div className="grid gap-3 md:grid-cols-3">
              <DollarInput label="Visa / Mastercard" value={formData.monthly_avg_visa_mc} onChange={(v) => set('monthly_avg_visa_mc', v)} testId="monthly-avg-visa-mc" />
              <DollarInput label="Discover" value={formData.monthly_avg_discover} onChange={(v) => set('monthly_avg_discover', v)} testId="monthly-avg-discover" />
              <DollarInput label="Amex" value={formData.monthly_avg_amex} onChange={(v) => set('monthly_avg_amex', v)} testId="monthly-avg-amex" />
            </div>
            {monthlyBreakdownTotal > 0 && (
              <div className="mt-2 text-xs text-muted-foreground flex items-center gap-1.5">
                <Receipt className="h-3.5 w-3.5" />
                Combined Monthly Average: <span className="font-semibold text-foreground">${monthlyBreakdownTotal.toLocaleString('en-US', { minimumFractionDigits: 2 })}</span>
              </div>
            )}
          </div>
          <div className="grid gap-3 md:grid-cols-2">
            <DollarInput label="Average Ticket" value={formData.avg_ticket} onChange={(v) => set('avg_ticket', v)} testId="avg-ticket" placeholder="Avg transaction size" />
            <DollarInput label="High Ticket" value={formData.high_ticket} onChange={(v) => set('high_ticket', v)} testId="high-ticket" placeholder="Largest expected transaction" />
          </div>
        </div>
      </div>

      {/* Processing Volume + Delivery Windows */}
      <div className="grid gap-4 md:grid-cols-2">
        <div className="border rounded-lg overflow-hidden">
          <SectionHeader>Processing Volume</SectionHeader>
          <div className="p-4 space-y-3">
            <div>
              <Label className="text-sm mb-1 block">Average Monthly Card Volume</Label>
              <div className="relative flex items-center gap-2">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">$</span>
                <Input type="number" value={formData.avg_monthly_volume} onChange={(e) => set('avg_monthly_volume', e.target.value)} className="pl-7 flex-1" placeholder="0.00" data-testid="avg-monthly-volume" />
                <span className="text-sm text-muted-foreground whitespace-nowrap">/ month</span>
              </div>
            </div>
            <div>
              <Label className="text-sm mb-1 block">Average Transaction Amount</Label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">$</span>
                <Input type="number" value={formData.avg_transaction_amount} onChange={(e) => set('avg_transaction_amount', e.target.value)} className="pl-7" placeholder="0.00" data-testid="avg-transaction-amount" />
              </div>
            </div>
          </div>
        </div>
        <div className="border rounded-lg overflow-hidden">
          <SectionHeader>Product / Service Delivery Windows</SectionHeader>
          <div className="p-4">
            <p className="text-sm text-muted-foreground mb-3">On average, Products / Services are delivered in</p>
            <div className="grid grid-cols-2 gap-3">
              {[
                { key: 'delivery_0_7', label: '0\u20137 Days' },
                { key: 'delivery_8_14', label: '8\u201314 Days' },
                { key: 'delivery_15_30', label: '15\u201330 Days' },
                { key: 'delivery_30_plus', label: '30+ Days' }
              ].map(d => (
                <label key={d.key} className="flex items-center gap-2 cursor-pointer">
                  <Checkbox checked={!!formData[d.key]} onCheckedChange={(v) => set(d.key, !!v)} data-testid={d.key} />
                  <span className="text-sm">{d.label}</span>
                </label>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Mode of Transaction + Pricing Program Detail */}
      <div className="grid gap-4 md:grid-cols-2">
        <div className="border rounded-lg overflow-hidden">
          <SectionHeader>Mode of Transaction</SectionHeader>
          <div className="p-4 space-y-3">
            {[
              { key: 'mode_in_person', label: 'In Person' },
              { key: 'mode_telephone', label: 'Telephone' },
              { key: 'mode_online', label: 'Online' }
            ].map(m => (
              <div key={m.key} className="flex items-center justify-between">
                <Label className="text-sm w-24">{m.label}</Label>
                <div className="relative flex-1 max-w-[200px]">
                  <Input type="number" min="0" max="100" value={formData[m.key]} onChange={(e) => set(m.key, e.target.value)} className="pr-7 text-right" data-testid={m.key} />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">%</span>
                </div>
              </div>
            ))}
            <div className={`text-center text-sm font-medium ${modeTotal === 100 ? 'text-green-600' : 'text-red-500'}`}>
              Must total 100% {modeTotal > 0 && `(Current: ${modeTotal}%)`}
            </div>
          </div>
        </div>
        <div className="border rounded-lg overflow-hidden">
          <SectionHeader>Pricing Program</SectionHeader>
          <div className="p-4 space-y-3">
            {/* Flat Rate Detail */}
            <div
              onClick={() => set('detailed_program', 'flat_rate_detail')}
              className={`border-2 rounded-lg p-3 cursor-pointer transition-colors ${formData.detailed_program === 'flat_rate_detail' ? 'border-[#3b82f6] bg-blue-50/30' : 'border-border'}`}
              data-testid="detail-flat-rate"
            >
              <div className="flex items-center gap-2 mb-1">
                <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${formData.detailed_program === 'flat_rate_detail' ? 'border-[#3b82f6]' : 'border-gray-300'}`}>
                  {formData.detailed_program === 'flat_rate_detail' && <div className="w-2 h-2 rounded-full bg-[#3b82f6]" />}
                </div>
                <span className="font-semibold text-sm">Flat Rate Pricing</span>
              </div>
              <div className="pl-6 space-y-0.5 text-xs text-muted-foreground">
                <div className="flex justify-between"><span>Swipe/Tap/Dip</span><span className="font-medium text-foreground">2.60% + $0.10</span></div>
                <div className="flex justify-between"><span>Non-qualified/Offline</span><span className="font-medium text-foreground">3.50% + $0.20</span></div>
              </div>
            </div>
            {/* Dual Price */}
            <div
              onClick={() => set('detailed_program', 'dual_price')}
              className={`border-2 rounded-lg p-3 cursor-pointer transition-colors ${formData.detailed_program === 'dual_price' ? 'border-[#3b82f6] bg-blue-50/30' : 'border-border'}`}
              data-testid="detail-dual-price"
            >
              <div className="flex items-center gap-2 mb-1">
                <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${formData.detailed_program === 'dual_price' ? 'border-[#3b82f6]' : 'border-gray-300'}`}>
                  {formData.detailed_program === 'dual_price' && <div className="w-2 h-2 rounded-full bg-[#3b82f6]" />}
                </div>
                <span className="font-semibold text-sm">Dual Price</span>
              </div>
              <div className="pl-6 space-y-0.5 text-xs text-muted-foreground">
                <div className="flex justify-between"><span>To Merchant</span><span className="font-medium text-foreground">0%</span></div>
                <div className="flex justify-between"><span>To Consumer</span><span className="font-medium text-foreground">4%</span></div>
              </div>
              <div className="mt-2 pl-6 flex items-start gap-1 text-[10px] text-amber-700 bg-amber-50 rounded p-1.5">
                <AlertTriangle className="h-3 w-3 shrink-0 mt-0.5" />
                <span>Must abide by local authority rules and display both prices clearly on menu or shelves.</span>
              </div>
            </div>
            {/* FTP Dual Price */}
            <div
              onClick={() => set('detailed_program', 'ftp_dual_price')}
              className={`border-2 rounded-lg p-3 cursor-pointer transition-colors ${formData.detailed_program === 'ftp_dual_price' ? 'border-[#3b82f6] bg-blue-50/30' : 'border-border'}`}
              data-testid="detail-ftp-dual-price"
            >
              <div className="flex items-center gap-2 mb-1">
                <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${formData.detailed_program === 'ftp_dual_price' ? 'border-[#3b82f6]' : 'border-gray-300'}`}>
                  {formData.detailed_program === 'ftp_dual_price' && <div className="w-2 h-2 rounded-full bg-[#3b82f6]" />}
                </div>
                <span className="font-semibold text-sm">FTP Dual Price: Free Placement under $30k or New Business</span>
              </div>
              <div className="pl-6 space-y-0.5 text-xs text-muted-foreground">
                <div className="flex justify-between"><span>To Merchant</span><span className="font-medium text-foreground">1%</span></div>
                <div className="flex justify-between"><span>To Consumer</span><span className="font-medium text-foreground">4%</span></div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Interchange Plus Pricing */}
      <div className="border rounded-lg overflow-hidden">
        <SectionHeader>Interchange Plus Pricing</SectionHeader>
        <div className="p-4 space-y-4">
          <div className="grid gap-4 md:grid-cols-2">
            <div>
              <Label className="text-sm mb-1 block">Basis Points</Label>
              <div className="relative">
                <Input type="number" step="0.01" value={formData.interchange_basis_points} onChange={(e) => set('interchange_basis_points', e.target.value)} className="pr-7" placeholder="0.40" data-testid="interchange-basis-points" />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">%</span>
              </div>
            </div>
            <div>
              <Label className="text-sm mb-1 block">Transaction Fees</Label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">$</span>
                <Input type="number" step="0.01" value={formData.interchange_transaction_fee} onChange={(e) => set('interchange_transaction_fee', e.target.value)} className="pl-7" placeholder="0.10" data-testid="interchange-transaction-fee" />
              </div>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-4">
            {['gross', 'net'].map(t => (
              <label key={t} className="flex items-center gap-2 cursor-pointer">
                <input type="radio" name="interchange_type" value={t} checked={formData.interchange_type === t} onChange={(e) => set('interchange_type', e.target.value)} className="w-4 h-4 accent-[#3b82f6]" />
                <span className="text-sm capitalize">{t} Interchange</span>
              </label>
            ))}
            <label className="flex items-center gap-2 cursor-pointer">
              <Checkbox checked={!!formData.interchange_dues_passthru} onCheckedChange={(v) => set('interchange_dues_passthru', !!v)} data-testid="dues-passthru" />
              <span className="text-sm">Dues and Assessments Passthru</span>
            </label>
          </div>
        </div>
      </div>

      {/* Fees and Charges */}
      <div className="border rounded-lg overflow-hidden">
        <SectionHeader>Fees and Charges</SectionHeader>
        <div className="p-4 grid gap-3 md:grid-cols-3">
          <FeeInput label="Monthly Service Fee" value={formData.monthly_service_fee} onChange={(v) => set('monthly_service_fee', v)} testId="fee-monthly-service" />
          <FeeInput label="Monthly Debit Access" value={formData.monthly_debit_access} onChange={(v) => set('monthly_debit_access', v)} testId="fee-monthly-debit" />
          <FeeInput label="Batch Fee" value={formData.batch_fee} onChange={(v) => set('batch_fee', v)} testId="fee-batch" />
          <FeeInput label="AVS Fee" value={formData.avs_fee} onChange={(v) => set('avs_fee', v)} testId="fee-avs" />
          <FeeInput label="Monthly Minimum" value={formData.monthly_minimum} onChange={(v) => set('monthly_minimum', v)} testId="fee-monthly-minimum" />
          <FeeInput label="Monthly Equipment Subscription" value={formData.monthly_equipment_subscription} onChange={(v) => set('monthly_equipment_subscription', v)} testId="fee-monthly-equipment" />
          <FeeInput label="Gateway Fee" value={formData.gateway_fee} onChange={(v) => set('gateway_fee', v)} testId="fee-gateway" />
          <FeeInput label="Gateway Trans Fee" value={formData.gateway_trans_fee} onChange={(v) => set('gateway_trans_fee', v)} testId="fee-gateway-trans" />
          <FeeInput label="Wireless Monthly" value={formData.wireless_monthly} onChange={(v) => set('wireless_monthly', v)} testId="fee-wireless-monthly" />
          <FeeInput label="Wireless Per Item" value={formData.wireless_per_item} onChange={(v) => set('wireless_per_item', v)} testId="fee-wireless-item" />
          <FeeInput label="PCI Fee" value={formData.pci_fee} onChange={(v) => set('pci_fee', v)} testId="fee-pci" />
          <FeeInput label="Non PCI Compliance Fee" value={formData.non_pci_compliance_fee} onChange={(v) => set('non_pci_compliance_fee', v)} testId="fee-non-pci" />
          <div /> {/* empty cell */}
          <FeeInput label="Annual Advantage Fee" value={formData.annual_advantage_fee} onChange={(v) => set('annual_advantage_fee', v)} testId="fee-annual-advantage" />
          <FeeInput label="Monthly Advantage Program" value={formData.monthly_advantage_program} onChange={(v) => set('monthly_advantage_program', v)} testId="fee-monthly-advantage" />
        </div>
      </div>

      {/* Navigation */}
      <div className="flex justify-between items-center pt-4 border-t">
        <Button type="button" variant="outline" onClick={() => onSaveDraft?.(formData)} data-testid="save-draft-btn">
          <Save className="h-4 w-4 mr-2" /> Exit & Save Draft
        </Button>
        <div className="flex items-center gap-3">
          <Button type="button" variant="outline" onClick={onPrevious} data-testid="previous-step-btn">
            <ArrowLeft className="h-4 w-4 mr-1" /> Previous
          </Button>
          <Button type="submit" disabled={saving} className="bg-[#3b82f6] hover:bg-[#2563eb]" data-testid="save-continue-btn">
            {saving ? 'Saving...' : 'Save & Continue \u2192'}
          </Button>
        </div>
      </div>
    </form>
  );
}

export default Step3Pricing;
